import { toBase64 } from "./bytes";
import type { HermesClient } from "./client";
import { HermesError } from "./errors";
import type { ServerRequest } from "./events";
import { arr, bool, obj, params, s, type Json, type JsonObject } from "./json";
import { mediaRefsIn } from "./media";
import {
  parseCommandsCatalog, parseConfigSet, parseCronManage, parseCronRun, parseMessages, parseModelOptions, parseProfile,
  parseProfileDetail, parseSessionSummary, parseSlashExec, parseSnapshot, parseUsage,
  type ConfigSetResult, type CronDeliveryTarget, type CronManageResult, type CronRun, type FileAttachResult,
  type ImageAttachResult, type ModelOptionsResult, type ProfileDetail, type ProfileSummary, type SessionSnapshot,
  type SessionSummary, type SlashCommand, type SlashExecResult, type TranscriptMessage, type UsageStats,
} from "./models";

/**
 * 按领域组织的强类型方法（对应 HermesKit 的 HermesAPI.swift 等）。方法名对应 gateway-contract 里的 RPC 名。
 * `profile` 为 undefined 表示默认 Bot。
 */
export class HermesAPI {
  constructor(readonly client: HermesClient) {}

  private call(method: string, p: Json = {}, opts?: { timeout?: number; waitForConnection?: number }): Promise<Json> {
    return this.client.call(method, p, opts);
  }

  private get broker() { return this.client.broker; }

  // MARK: 会话

  async createSession(profile?: string, title?: string): Promise<SessionSnapshot> {
    return parseSnapshot(await this.call("session.create", params({ profile, title, source: "timibot" }), { timeout: 60 }), "session.create");
  }

  /** 接入一个存储会话（如果它在网关里还活着就直接复用） */
  async resumeSession(storedID: string, profile?: string): Promise<SessionSnapshot> {
    return parseSnapshot(await this.call("session.resume", params({ session_id: storedID, profile, source: "timibot" }), { timeout: 90 }), "session.resume");
  }

  async listSessions(profile?: string, limit = 100): Promise<SessionSummary[]> {
    const raw = obj(await this.call("session.list", params({ profile, limit })));
    return (arr(raw?.sessions) ?? []).map(parseSessionSummary).filter((x): x is SessionSummary => !!x);
  }

  async sessionHistory(sessionID: string): Promise<TranscriptMessage[]> {
    return parseMessages(obj(await this.call("session.history", { session_id: sessionID }))?.messages);
  }

  async renameSession(sessionID: string, title: string): Promise<void> {
    await this.call("session.title", { session_id: sessionID, title });
  }

  async deleteSession(storedID: string, profile?: string): Promise<void> {
    await this.call("session.delete", params({ session_id: storedID, profile }));
  }

  async closeSession(sessionID: string): Promise<void> {
    await this.call("session.close", { session_id: sessionID }, { waitForConnection: 0 });
  }

  async sessionUsage(sessionID: string): Promise<UsageStats | undefined> {
    return parseUsage(await this.call("session.usage", { session_id: sessionID }));
  }

  // MARK: 对话

  /** 发送一轮用户消息。会话忙时网关会自动排队 / 转向（status 反映结果）。 */
  async submitPrompt(sessionID: string, text: string): Promise<{ status?: string }> {
    const raw = obj(await this.call("prompt.submit", { session_id: sessionID, text, surface: "timibot" }, { timeout: 60 }));
    return { status: s(raw?.status) };
  }

  async interrupt(sessionID: string): Promise<void> {
    await this.call("session.interrupt", { session_id: sessionID }, { timeout: 30 });
  }

  /** 上传图片（base64），排队到下一轮对话 */
  async attachImage(sessionID: string, data: Uint8Array, filename: string): Promise<ImageAttachResult> {
    const raw = obj(await this.call("image.attach_bytes", {
      session_id: sessionID, content_base64: toBase64(data), filename,
    }, { timeout: 120 }));
    return { attached: bool(raw?.attached) ?? false, name: s(raw?.name), path: s(raw?.path), message: s(raw?.message) };
  }

  async detachImage(sessionID: string, path: string): Promise<void> {
    await this.call("image.detach", { session_id: sessionID, path });
  }

  /** 上传普通文件（PDF、Word、表格…）到会话工作区 */
  async attachFile(sessionID: string, data: Uint8Array, filename: string, mime: string): Promise<FileAttachResult> {
    const raw = obj(await this.call("file.attach", {
      session_id: sessionID, data_url: `data:${mime};base64,${toBase64(data)}`, name: filename,
    }, { timeout: 180 }));
    return {
      refText: s(raw?.ref_text) ?? `@file:${s(raw?.ref_path) ?? filename}`,
      path: s(raw?.path),
      name: s(raw?.name) ?? filename,
    };
  }

  // MARK: Bot（profiles）

  async listProfiles(): Promise<{ profiles: ProfileSummary[]; botModeProtocol: boolean }> {
    const raw = obj(await this.call("profiles.list", {}));
    return {
      profiles: (arr(raw?.profiles) ?? []).map(parseProfile).filter((p): p is ProfileSummary => !!p),
      botModeProtocol: bool(raw?.bot_mode_protocol) ?? false,
    };
  }

  async describeProfile(name: string): Promise<ProfileDetail> {
    return parseProfileDetail(await this.call("profiles.describe", { name }));
  }

  async profileAvatar(name: string): Promise<string | undefined> {
    const raw = obj(await this.call("profiles.get_asset", { name, asset: "avatar" }));
    if (!bool(raw?.found)) return undefined;
    const data = s(raw?.data);
    if (!data) return undefined;
    return data.startsWith("data:") ? data : `data:${s(raw?.mime) ?? "image/png"};base64,${data}`;
  }

  /** 删除一个 Bot（Hermes 原生接口）：目录连同对话、记忆、技能、定时任务一起删掉，不能恢复 */
  async deleteProfile(name: string): Promise<void> {
    await this.broker.sendJSON("DELETE", `/api/profiles/${encodeURIComponent(name)}`, {});
  }

  async createProfile(name: string, description?: string, soul?: string, cloneFrom?: string): Promise<void> {
    await this.call("profiles.create", params({ name, description, soul, clone_from: cloneFrom }), { timeout: 60 });
  }

  async configureProfile(name: string, description?: string, soul?: string): Promise<void> {
    await this.call("profiles.configure", params({ name, description, soul }));
  }

  /** 改 Bot 的显示名：写到 ui_meta["hermes-bots"].title，保留里面的其它字段 */
  async setBotTitle(name: string, title: string, meta?: JsonObject): Promise<void> {
    await this.call("profiles.configure", { name, ui_meta: { "hermes-bots": { ...(meta ?? {}), title } } });
  }

  // MARK: 定时任务

  async listCronJobs(profile?: string): Promise<CronManageResult> {
    return parseCronManage(await this.call("cron.manage", params({ action: "list", include_disabled: true, profile })));
  }

  /** action: pause / resume / remove */
  async manageCronJob(action: string, name: string, profile?: string): Promise<CronManageResult> {
    return parseCronManage(await this.call("cron.manage", params({ action, name, profile })));
  }

  async addCronJob(name: string | undefined, schedule: string, prompt: string, deliver: string | undefined, profile?: string): Promise<CronManageResult> {
    return parseCronManage(await this.call("cron.manage", params({ action: "add", name, schedule, prompt, deliver, profile })));
  }

  async cronJobDetail(id: string, profile?: string): Promise<{ id: string; prompt?: string; deliver?: string }> {
    const o = obj(await this.broker.getJSON(`/api/cron/jobs/${encodeURIComponent(id)}`, { profile }));
    return { id: s(o?.id) ?? id, prompt: s(o?.prompt), deliver: s(o?.deliver) };
  }

  async cronDeliveryTargets(profile?: string): Promise<CronDeliveryTarget[]> {
    const o = obj(await this.broker.getJSON("/api/cron/delivery-targets", { profile }));
    return (arr(o?.targets) ?? []).map(obj).filter(Boolean).map((t) => ({
      id: s(t!.id) ?? "", name: s(t!.name) ?? s(t!.id) ?? "", home_target_set: bool(t!.home_target_set),
    })).filter((t) => t.id);
  }

  async updateCronJob(id: string, updates: JsonObject, profile?: string): Promise<void> {
    await this.broker.sendJSON("PUT", `/api/cron/jobs/${encodeURIComponent(id)}`, { updates }, { profile });
  }

  /** 某个定时任务最近的运行（新的在前） */
  async cronRuns(jobID: string, profile?: string, limit = 20): Promise<CronRun[]> {
    const raw = await this.broker.getJSON(`/api/cron/jobs/${encodeURIComponent(jobID)}/runs`, { limit: String(limit), profile });
    const rows = arr(raw) ?? arr(obj(raw)?.runs) ?? arr(obj(raw)?.sessions) ?? [];
    return rows.map(parseCronRun).filter((r): r is CronRun => !!r);
  }

  // MARK: 技能

  async listSkills(profile?: string): Promise<Record<string, string[]>> {
    const o = obj(obj(await this.call("skills.manage", params({ action: "list", profile })))?.skills);
    const out: Record<string, string[]> = {};
    for (const [k, v] of Object.entries(o ?? {})) out[k] = (arr(v) ?? []).filter((x): x is string => typeof x === "string");
    return out;
  }

  async searchSkillHub(query: string, profile?: string): Promise<{ name?: string; description?: string; source?: string; trust?: string; identifier?: string }[]> {
    const o = obj(await this.call("skills.manage", params({ action: query ? "search" : "browse", query: query || undefined, profile }), { timeout: 60 }));
    return (arr(o?.items) ?? []).map(obj).filter(Boolean).map((i) => ({
      name: s(i!.name), description: s(i!.description), source: s(i!.source), trust: s(i!.trust), identifier: s(i!.identifier),
    }));
  }

  async installSkill(identifier: string, profile?: string): Promise<boolean> {
    return bool(obj(await this.call("skills.manage", params({ action: "install", query: identifier, profile }), { timeout: 180 }))?.installed) ?? false;
  }

  // MARK: 模型 / 配置 / 命令

  async modelOptions(sessionID?: string, profile?: string, includeUnconfigured = false): Promise<ModelOptionsResult> {
    return parseModelOptions(await this.call("model.options", params({
      session_id: sessionID, profile, include_unconfigured: includeUnconfigured ? true : undefined,
    }), { timeout: 60 }));
  }

  /**
   * 切换模型（与 /model 同一个解析器：`模型 --provider 提供方 [--session|--global]`）。
   * persist：同时设为以后新对话的默认模型；否则只改这段对话。
   */
  async switchModel(sessionID: string, model: string, provider: string | undefined, persist = false, confirmExpensive = false): Promise<ConfigSetResult> {
    let value = model;
    // “custom” 是自定义地址那一行的占位名，不是真的提供方
    if (provider && provider !== "custom") value += ` --provider ${provider}`;
    value += persist ? " --global" : " --session";
    return this.setConfig("model", value, sessionID, undefined, confirmExpensive);
  }

  /** 只设置新对话的默认模型（网页后台“模型”页同一个接口）。返回提示文字表示需要确认后带 confirmExpensive 重试。 */
  async setDefaultModel(model: string, provider: string, profile?: string, confirmExpensive = false): Promise<string | undefined> {
    const body: JsonObject = { scope: "main", provider, model };
    if (confirmExpensive) body.confirm_expensive_model = true;
    const r = obj(await this.broker.sendJSON("POST", "/api/model/set", body, { profile }));
    if (bool(r?.confirm_required)) return s(r?.confirm_message) ?? "这个模型价格较高，确定使用吗？";
    return undefined;
  }

  async saveModelKey(slug: string, apiKey: string, profile?: string): Promise<void> {
    await this.call("model.save_key", params({ slug, api_key: apiKey, profile }), { timeout: 60 });
  }

  async disconnectModelProvider(slug: string, profile?: string): Promise<void> {
    await this.call("model.disconnect", params({ slug, profile }));
  }

  async setConfig(key: string, value: Json, sessionID?: string, scope?: string, confirmExpensive = false): Promise<ConfigSetResult> {
    return parseConfigSet(await this.call("config.set", params({
      key, value, session_id: sessionID, scope, confirm_expensive_model: confirmExpensive ? true : undefined,
    })));
  }

  async commandsCatalog(sessionID?: string, profile?: string): Promise<SlashCommand[]> {
    return parseCommandsCatalog(await this.call("commands.catalog", params({ session_id: sessionID, profile })));
  }

  /** 结构化分发斜杠命令（桌面端的主路径） */
  async dispatchCommand(name: string, arg: string | undefined, sessionID?: string, profile?: string): Promise<SlashExecResult> {
    return parseSlashExec(await this.call("command.dispatch", params({ name, arg, session_id: sessionID, profile }), { timeout: 120 }));
  }

  async execSlash(sessionID: string, command: string): Promise<SlashExecResult> {
    return parseSlashExec(await this.call("slash.exec", { session_id: sessionID, command }, { timeout: 120 }));
  }

  // MARK: 服务端请求的回答

  /** approval：once / session / always / deny */
  answerApproval(request: ServerRequest, choice: string): void {
    this.client.respond(request, { result: { choice } });
  }

  // MARK: 文件

  /** 下载服务器上的一个文件（生成的图片/视频、上传的图片等）：走 `/api/fs/download`，服务器会拒绝 .env 等敏感文件 */
  downloadServerFile(path: string): Promise<{ data: Uint8Array; mime?: string }> {
    return this.broker.download("/api/fs/download", { path });
  }

  /** 生成的文件引用（例如 file.attach 的结果）里的第一份媒体 */
  static firstMediaSource(refText: string): string | undefined {
    return mediaRefsIn(refText)[0]?.source;
  }

  // MARK: connector 的管理接口（/timibot/v1/*，经同一条加密隧道）

  async shareMe(): Promise<{ role?: string; profiles?: string[]; voice?: { key?: string; region?: string } }> {
    const o = obj(await this.broker.getJSON("/timibot/v1/me"));
    const voice = obj(o?.voice);
    return {
      role: s(o?.role),
      profiles: (arr(o?.profiles) ?? []).filter((x): x is string => typeof x === "string"),
      voice: voice ? { key: s(voice.key), region: s(voice.region) } : undefined,
    };
  }

  async ping(): Promise<boolean> {
    return bool(obj(await this.call("ping", {}, { timeout: 10 }))?.pong) ?? false;
  }
}

export function rpcErrorMessage(e: unknown): string {
  if (e instanceof HermesError) return e.message;
  return e instanceof Error ? e.message : String(e);
}

